import SessionCache, { MINUTES } from './SessionCache';
import type { IAMContactDetailsJsonSchema } from './types/IAMContactDetailsJsonSchema';
import type { IAMPersonalInfoJsonSchema } from './types/IAMPersonalInfoJsonSchema';
import type { IAMTaxReceiptJsonSchema } from './types/IAMTaxReceiptJsonSchema';
import type { IAMDonorReplyJsonSchema } from './types/IAMDonorReplyJsonSchema';

type DonorServiceClientDependencies = {
    readonly getAccessToken: () => Promise<string>
}

const baseUrl = import.meta.env.VITE_APE_DONATION_API_BASE_URL;

export class DonorServiceError extends Error {
    constructor(message: string, public cause?: unknown) {
        super(message, { cause });
        this.name = this.constructor.name;
    }
}

const sessionCache = SessionCache<IAMDonorReplyJsonSchema>(30 * MINUTES);

type CallParams = {
    method: "GET" | "PUT" | "DELETE";
    url: string;
    data?: unknown;
    signal?: AbortSignal;
}

export class DonorService {
    private readonly getAccessToken: () => Promise<string>;
    constructor({ getAccessToken }: DonorServiceClientDependencies) {
        this.getAccessToken = getAccessToken;
    }

    async fetchDonorRecord(): Promise<IAMDonorReplyJsonSchema> {
        return sessionCache.get() || sessionCache.set(await this.call({ method : 'GET', url : '/' }));
    }

    async updatePersonalInfo(data: IAMPersonalInfoJsonSchema, signal?: AbortSignal): Promise<void> {
        sessionCache.reset();
        return this.call({ method : 'PUT', url : '/personal-information', data, signal });
    }

    async updateContactDetails(data: IAMContactDetailsJsonSchema, signal?: AbortSignal): Promise<void> {
        sessionCache.reset();
        return this.call({ method : 'PUT', url : '/contact-details', data, signal });
    }

    async fetchTaxReceipt(data: IAMTaxReceiptJsonSchema): Promise<{ url: string }> {
        if (data.href) return { url: data.href };
        const searchParams = new URLSearchParams(Object.entries(data));
        searchParams.set('dateIssued', data.dateIssued.slice(0, 10));
        if (!data.href) searchParams.delete('href');
        return this.call({ method : 'GET', url : `/tax-receipt?${searchParams.toString()}` });
    };

    private async call<T>(
        { method, url, data, signal }: CallParams,
    ): Promise<T> {
        const headers = new Headers({
            Authorization: `Bearer ${await this.getAccessToken()}`,
            Accept: 'application/json',
        });
        if (method === 'PUT') headers.append('Content-Type', 'application/json');

        try {
            const response = await fetch(new URL(`iam/v1/me${url}`, baseUrl), {
                method,
                headers,
                credentials: 'omit',
                body: data ? JSON.stringify(data) : undefined,
                signal,
            });
            const content = response.headers
                .get('Content-Type')
                ?.includes('application/json')
                ? await response.json()
                : await response.text();

            if (!response.ok) throw new DonorServiceError('API has returned an error', content);
            return content as T;
        } catch (e) {
            console.error('Error while calling the Donation API', e);
            if (e instanceof DonorServiceError) throw e;
            throw new DonorServiceError('Unable to call the Donor API', e);
        }
    }
}
